//core
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useField, useFormikContext } from "formik";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { FormControl, FormHelperText, InputAdornment, IconButton} from "@mui/material";
import { isValid } from "date-fns";


//locales
import ruLocale from "date-fns/locale/ru";
import ukLocale from "date-fns/locale/uk";
import enLocale from "date-fns/locale/en-US";
import esLocale from "date-fns/locale/es";
import trLocale from "date-fns/locale/tr";
import koLocale from "date-fns/locale/ko";
import elLocale from "date-fns/locale/el";
import frLocale from "date-fns/locale/fr";
import ptLocale from "date-fns/locale/pt";
import thLocale from "date-fns/locale/th";
import viLocale from "date-fns/locale/vi";
import idLocale from "date-fns/locale/id";

//styles
import styles from './styles/styles.module.scss';
import Calendar from "@mui/icons-material/Event";

//types
interface IMuiFieldProps {
  id?: string;
  name: string;
  autoComplete?: string;
  label: string;
  value?: any;
  secondValue?: any;
  maxDate?: any,
  minDate?: any,
  //type?: React.InputHTMLAttributes<unknown>['type'];

  hideError?: boolean,
  placeholder?: string,
  disabled?: boolean,
  className?: string,
  helperText?: string,
  dataId?: string,
  InputProps?: any,
  error?: boolean | string,
  validation?: (value: any) => any,
  validate?: (value: any) => any,
  onChange?: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>;
  inputFormat?: string;
  views?: ('year' | 'month' | 'day')[];
}

//locales
const localeMap = {
  en: enLocale,
  ru: ruLocale,
  uk: ukLocale,
  es: esLocale,
  tr: trLocale,
  ko: koLocale,
  el: elLocale,
  fr: frLocale,
  pt: ptLocale,
	th: thLocale,
	vi: viLocale,
	id: idLocale,
};

export const MuiSingleDatePicker = (props: IMuiFieldProps) => {
  const [field, meta] = useField(props);
  const { t, i18n } = useTranslation();
  const { setFieldValue } = useFormikContext();

  const {
    id,
    name,
    autoComplete,
    label,
    value,
    secondValue,
    disabled = false,
    maxDate,
    minDate,
    error = '',
    hideError = false,
    inputFormat = '',
    views = ['year', 'month', 'day'],
  } = props;

  const [fieldVal, setFieldVal] = React.useState<Date | null>(() => {
    if (!value || value === '' || value === null || value === undefined) return null;
    const date = new Date(value);
    return isValid(date) ? date : null;
  });
  const [hasValidationError, setHasValidationError] = useState<boolean>(false);

  const [open, setOpen] = useState(false);
  const [locale, setLocale] = useState<object>(localeMap.en);

  // Validate date function
  const validateDate = (date: Date | null): boolean => {
    if (!date) return true; // null is considered valid (empty field)

    // Check if date is valid
    if (!isValid(date)) {
      return false;
    }

    // Check against min/max dates if provided
    if (minDate && date < new Date(minDate)) {
      return false;
    }

    if (maxDate && date > new Date(maxDate)) {
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (!value || value === '' || value === null || value === undefined) {
      setFieldVal(null);
      return;
    }
    const date = new Date(value);
    setFieldVal(isValid(date) ? date : null);
  }, [value]);

  useEffect(() => {
    if (i18n.language === 'ru') {
      setLocale(localeMap.ru);
    }
    if (i18n.language === 'en') {
      setLocale(localeMap.en);
    }
    if (i18n.language === 'uk') {
      setLocale(localeMap.uk);
    }
    if (i18n.language === 'es') {
      setLocale(localeMap.es);
    }
    if (i18n.language === 'tr') {
      setLocale(localeMap.tr);
    }
    if (i18n.language === 'ko') {
      setLocale(localeMap.ko);
    }
    if (i18n.language === 'el') {
      setLocale(localeMap.el);
    }
    if (i18n.language === 'fr') {
      setLocale(localeMap.fr);
    }
    if (i18n.language === 'pt') {
      setLocale(localeMap.pt);
    }
	  if (i18n.language === 'th') {
		  setLocale(localeMap.th);
	  }
	  if (i18n.language === 'vi') {
		  setLocale(localeMap.vi);
	  }
	  if (i18n.language === 'id') {
		  setLocale(localeMap.id);
	  }
  }, [i18n.language]);

  useEffect(() => {
    if (fieldVal !== null) {
      if (name === 'at_end_show') {
        if (new Date(fieldVal) < new Date(secondValue)) {
          setFieldValue(field.name, secondValue);
          setFieldVal(secondValue);
        }
      }
      if (name === 'at_beginning_show') {
        if (new Date(fieldVal) > new Date(secondValue)) {
          setFieldValue('at_end_show', fieldVal);
          setFieldVal(fieldVal);
        }
      }
    }

  }, [fieldVal, minDate]);

  const handleDateChange = (newValue: Date | null) => {
    setFieldVal(newValue);

    const isValidDate = validateDate(newValue);
    setHasValidationError(!isValidDate);

    // Only call setFieldValue if the date is valid
    if (isValidDate) {
      setFieldValue(field.name, newValue);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
      <div className={styles.formField}>
        <FormControl fullWidth>
          <DatePicker
            open={open}
            onOpen={() => setOpen(true)}
            onClose={handleClose}
            label={label}
            disabled={disabled}
            value={fieldVal}
            format={!!inputFormat ? inputFormat : i18n.language === 'en' ? 'MM/dd/yyyy' : 'dd/MM/yyyy'}
            views={views}
            {...(maxDate ? {maxDate: new Date(maxDate)} : {})}
            {...(minDate ? {minDate: new Date(minDate)} : {})}
            onChange={handleDateChange}
            slotProps={{
              popper: {
                disablePortal: true
              },
              textField: {
                size: 'small',
                id: id,
                className: `${styles.input} ${ 
                  !!(meta.touched && !!meta.error && meta.error !== 'error') || hasValidationError 
                    ? styles.inputError 
                    : styles.inputErrorDef 
                }`,
                ...(hideError ? {error: false} : (meta.touched && !!meta.error && meta.error !== 'error') || hasValidationError ? { error: true } : {error: false})
              },
              actionBar: ({ wrapperVariant }) => {
                return {
                  actions: wrapperVariant === 'desktop' ? [] : ['accept'],
                }
              },
            }}
          />
          { (meta.touched && !!meta.error && meta.error !== 'error') || hasValidationError ? (
            <FormHelperText error>
              {hasValidationError ? t("common.validation.invalid_date") : meta.error}
            </FormHelperText>
          ) : null }
        </FormControl>
      </div>
    </LocalizationProvider>
  );
};
